import React from "react";
import {
    Box,
    Grid,
    Heading,
    Text,
    VStack,
    HStack,
    Icon, useBreakpointValue,
} from "@chakra-ui/react";
import colors from "../colors";
import {
    SiReact, SiJavascript,
    SiTypescript, SiNextdotjs,
    SiNodedotjs,
    SiChakraui,
    SiGit, SiGithub, SiSpringboot, SiOracle, SiHtml5, SiCss3
} from 'react-icons/si';
import {FaJava} from "react-icons/fa";
import {VscAzure} from "react-icons/vsc";
import {MdComputer} from "react-icons/md";

function Projects({isDark}) {
    const gridTemplate = {base: "1fr", md: "2fr 1fr"};
    const isMobile = useBreakpointValue({base: true, md: false});

    // Colors
    const primaryText = isDark ? colors.brand[50] : colors.gray[700];
    const secondaryText = isDark ? colors.lightBg : colors.gray[800];
    const accentColor = isDark ? colors.teal[500] : colors.brand[500];
    const techBg = isDark ? colors.darkBg : colors.brand[60]
    const borderColor = isDark ? colors.teal[500] : colors.brand[500];

    // Tech stack items
    const techStack = [
        {name: 'React', icon: SiReact},
        {name: 'JavaScript', icon: SiJavascript},
        {name: 'TypeScript', icon: SiTypescript},
        {name: 'HTML', icon: SiHtml5},
        {name: 'CSS', icon: SiCss3},
        {name: 'Next.js', icon: SiNextdotjs},
        {name: 'Node.js', icon: SiNodedotjs},
        {name: 'Chakra UI', icon: SiChakraui},
        {name: 'Java', icon: FaJava},
        {name: 'Spring Boot', icon: SiSpringboot},
        {name: 'Oracle SQL', icon: SiOracle},
        {name: 'Git', icon: SiGit},
        {name: 'GitHub', icon: SiGithub},
        {name: 'Azure', icon: VscAzure},
        {name: 'RESTful API', icon: MdComputer},
    ];

    // Timeline items
    const timeline = [
        {
            year: "2019",
            title: "Joined Walmart Global Tech",
            text: "Started as Software Engineer I focusing on front-end performance.",
        },
        {
            year: "2020",
            title: "Enterprise Development",
            text: "Expanded scalable in-store tools and digital integrations.",
        },
        {
            year: "2021",
            title: "Engineer II Promotion",
            text: "Led React feature pipelines and API integration efforts.",
        },
        {
            year: "2022",
            title: "Sam’s Club Transition",
            text: "Modernized UI and CI/CD pipelines with Azure + Looper.",
        },
        {
            year: "2023",
            title: "Feature Lead, Sam’s Cash",
            text: "Drove member reward rollout and enhanced observability.",
        },
        {
            year: "2024",
            title: "Optimization & Impact",
            text: "Scaled apps and improved performance monitoring.",
        },
    ];

    return (
        <Box px={{base: 4, md: 8}} py={10} minH="100vh">
            <Box px={{base: 4, md: 8}} py={2} >
                {/* --- HEADER --- */}
                <Heading
                    size="xl"
                    mb={8}
                    textAlign="center"
                    color={accentColor}
                >
                    Projects & Experience
                </Heading>

                {/* --- PROJECTS + TECH STACK --- */}
                <Grid
                    templateColumns={gridTemplate}
                    gap={8}
                    alignItems="stretch"
                >
                    {/* --- PROJECTS LIST --- */}
                    <Box
                        p={6}
                        borderRadius="xl"
                        bg={techBg}
                        boxShadow="md"
                        border={`1px solid ${borderColor}`}
                    >
                        <Heading size="md" mb={4} color={accentColor}>
                            Featured Work
                        </Heading>

                        <VStack align="start" spacing={3}>
                            {[
                                {
                                    name: "Walmart Pay",
                                    desc: "Enhanced checkout experience improving UI responsiveness and streamlining payment flow across web and mobile.",
                                },
                                {
                                    name: "Walmart Picklist",
                                    desc: "Optimized in-store fulfillment tools that reduced associate task times and improved accuracy.",
                                },
                                {
                                    name: "Walmart Ad Carousel",
                                    desc: "Built dynamic ad components using React and API-driven data, increasing engagement through animation.",
                                },
                                {
                                    name: "Sam’s Club Quest",
                                    desc: "Developed internal React tools with Azure and Docker, automated CI/CD using Looper pipelines.",
                                },
                                {
                                    name: "Sam’s Cash Awards",
                                    desc: "Led front-end rollout for member rewards, distributing over $870K in Sam’s Cash benefits.",
                                },
                                {
                                    name: "Analytics & Optimization",
                                    desc: "Enhanced Splunk dashboards for app health visibility, supporting $2B+ in promotions.",
                                },
                            ].map((proj, i) => (
                                <Box key={proj.name}>
                                    <HStack align="start" spacing={3}>
                                        <Box
                                            width="6px"
                                            height="6px"
                                            borderRadius="full"
                                            bg={accentColor}
                                            mt={2}
                                        />
                                        <Box>
                                            <Text
                                                as="span"
                                                fontWeight="bold"
                                                color={accentColor}
                                                fontSize="sm"
                                            >
                                                {proj.name}
                                            </Text>
                                            <Text fontSize="sm" color={primaryText}>
                                                {" "}
                                                – {proj.desc}
                                            </Text>
                                        </Box>
                                    </HStack>
                                    {i < 5 && (
                                        <Box
                                            borderBottom="1px solid"
                                            borderColor={isDark ? "gray.700" : "gray.400"}
                                            my={2}
                                            w="95%"
                                        />
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    </Box>

                    {/* --- TECH STACK --- */}
                    <Box
                        p={6}
                        borderRadius="xl"
                        bg={techBg}
                        boxShadow="md"
                        border={`1px solid ${borderColor}`}
                        display="flex"
                        flexDir="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Heading size="md" mb={4} color={accentColor}>
                            Tech Stack
                        </Heading>
                        <HStack align="start" wrap="wrap" justify="center" gap={12}>
                            {techStack.map((tech) => (
                                <VStack key={tech.name} spacing={1}>
                                    <Icon as={tech.icon} boxSize={7} color={secondaryText}/>
                                    <Text fontSize="xs" color={primaryText}>
                                        {tech.name}
                                    </Text>
                                </VStack>
                            ))}
                        </HStack>
                        <Text fontSize="xs" mt={4} color={accentColor}>
                            Frontend • Backend • CI/CD • Cloud Integration
                        </Text>
                    </Box>
                </Grid>

                <Box
                    textAlign="center"
                    mt={{ base: 20, md: 20 }}
                    mb={{ base: 0, md: 10 }}
                    width="100%"
                >
                    <Box
                        position="relative"
                        mx={{ base: 0, md: "auto" }}
                        w={{ base: "100%", md: "90%" }}
                    >
                        {/* Divider line */}
                        <Box
                            borderBottom="2px solid"
                            borderColor={borderColor}
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            transform="translateY(-50%)"
                            zIndex={0}
                        />

                        {/* Centered text over divider */}
                        <Box
                            position="relative"
                            display="inline-block"
                            bg={isDark ? colors.darkBg : colors.brand[100]}
                            px={6}
                            zIndex={1}
                            transform="translateY(-50%)"
                            border={`1px solid ${borderColor}`}
                            boxShadow="md"
                            ml={{ base: 4, md: "auto" }}
                            mr={{ base: 4, md: "auto" }}
                        >
                            <Text
                                fontSize={{ base: "md", md: "lg" }}
                                color={primaryText}
                                fontWeight="medium"
                            >
                                The journey doesn’t stop at the tools — here’s how my career evolved over time.
                            </Text>
                        </Box>
                    </Box>
                </Box>


                {/* --- TIMELINE --- */}
                <Heading size="md" textAlign="center" mb={isMobile ? 30 : 50} color={accentColor}>
                    Career Timeline
                </Heading>
                {isMobile ? (
                    // 📱 MOBILE TIMELINE VIEW
                    <Box position="relative" mt={{ base: 10, md: 60 }} mb={32}>
                        {/* central vertical line */}
                        <Box
                            position="absolute"
                            left="50%"
                            top="0"
                            width="2px"
                            height="100%"
                            bg={isDark ? "gray.600" : "gray.300"}
                            transform="translateX(-50%)"
                            zIndex={0}
                        />

                        <VStack spacing={12} position="relative" zIndex={2}>
                            {timeline.map((item, idx) => (
                                <Box key={idx} position="relative" w="full">
                                    {/* Dot */}
                                    <Box
                                        position="absolute"
                                        left="50%"
                                        top="0"
                                        transform="translate(-50%, -50%)"
                                        width="14px"
                                        height="14px"
                                        borderRadius="full"
                                        bg={isDark ? "teal.300" : "blue.500"}
                                        border="2px solid"
                                        borderColor={isDark ? "gray.800" : "white"}
                                        zIndex={2}
                                    />

                                    {/* Card */}
                                    <Box
                                        mt={4}
                                        mx="auto"
                                        bg={techBg}
                                        border="1px solid"
                                        borderColor={isDark ? "gray.600" : "gray.200"}
                                        borderRadius="xl"
                                        p={4}
                                        w="90%"
                                        boxShadow="md"
                                        textAlign="center"
                                        zIndex={3}
                                    >
                                        <Text fontSize="xs" fontWeight="bold" color={accentColor}>
                                            {item.year}
                                        </Text>
                                        <Text fontSize="xs" fontWeight="semibold" color={primaryText} mt={1}>
                                            {item.title}
                                        </Text>
                                        <Text fontSize="10px" color={secondaryText} mt={1}>
                                            {item.text}
                                        </Text>
                                    </Box>
                                </Box>
                            ))}
                        </VStack>
                    </Box>
                ) : (
                    // 🖥 DESKTOP TIMELINE VIEW (your original version)
                    <Box position="relative" mt={{ base: 10, md: 60 }} mb={32}>
                        {/* central horizontal line */}
                        <Box
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="2px"
                            bg={isDark ? "gray.600" : "gray.300"}
                            transform="translateY(-50%)"
                            zIndex={0}
                        />

                        {/* timeline grid */}
                        <Grid
                            templateColumns={`repeat(${timeline.length}, 1fr)`}
                            gap={0}
                            position="relative"
                            zIndex={2}
                            alignItems="center"
                            mt={{ base: 24, md: 32 }}
                            pb={{ base: 12, md: 16 }}
                        >
                            {timeline.map((item, idx) => {
                                const isAbove = idx % 2 === 0;
                                return (
                                    <Box
                                        key={idx}
                                        position="relative"
                                        textAlign="center"
                                        px={4}
                                        minW={{ base: "120px", md: "0" }}
                                    >
                                        {/* Tick mark */}
                                        <Box
                                            position="absolute"
                                            left="50%"
                                            top={isAbove ? "calc(50% - 90px)" : "calc(50%)"}
                                            transform="translateX(-50%)"
                                            width="2px"
                                            height="90px"
                                            bg={isDark ? "gray.500" : "gray.400"}
                                            zIndex={1}
                                        />

                                        {/* Dot */}
                                        <Box
                                            position="absolute"
                                            left="50%"
                                            top="50%"
                                            transform="translate(-50%, -50%)"
                                            width="14px"
                                            height="14px"
                                            borderRadius="full"
                                            bg={isDark ? "teal.300" : "blue.500"}
                                            border="2px solid"
                                            borderColor={isDark ? "gray.800" : "white"}
                                            zIndex={2}
                                        />

                                        {/* Info card */}
                                        <Box
                                            position="absolute"
                                            left="50%"
                                            top={isAbove ? "calc(50% - 190px)" : "calc(50% + 85px)"}
                                            transform="translateX(-50%)"
                                            bg={techBg}
                                            border="1px solid"
                                            borderColor={isDark ? "gray.600" : "gray.200"}
                                            borderRadius="xl"
                                            p={3}
                                            w={{ base: "150px", md: "180px" }}
                                            boxShadow="md"
                                            zIndex={3}
                                        >
                                            <Text fontSize="xs" fontWeight="bold" color={accentColor}>
                                                {item.year}
                                            </Text>
                                            <Text fontSize="xs" fontWeight="semibold" color={primaryText} mt={1}>
                                                {item.title}
                                            </Text>
                                            <Text fontSize="10px" color={secondaryText} mt={1}>
                                                {item.text}
                                            </Text>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Grid>
                    </Box>
                )}
            </Box>
        </Box>)
}

export default Projects;
